import { useQuery, useQueryClient } from "react-query";
import { supabase } from "../../../../../config/supabaseClient";
import { StaticVariables } from "../../../../../Utils/StaticVariables";
import {
  getComponentsTableName as getSurfaceComponentsTableName,
  WEBSITE_TEMPLATE_SURFACE,
} from "../../../../../Utils/StayUpToDate/componentSurface";
import { pickFirstNonEmptyImageField } from "../../../../../Utils/StayUpToDate/RenderComponent";
import { getActiveCreatorPageId } from "../../../../../features/creator-pages/storage";
import {
  getComponentsTableName as getPagesComponentsTableName,
  isCreatorPagesMode,
} from "../../../../../Utils/componentsTable";
import { syncTemplateComponentCardImageIfNeeded } from "../../../MasterTemplateEditor/templateComponentCardImageSync";

/** Same resolution as useLoadAllCollectionComponents (incl. creator-pages → page_components). */
function getComponentsTableName(from?: string) {
  if (from === WEBSITE_TEMPLATE_SURFACE) {
    return getSurfaceComponentsTableName(from);
  }
  return getPagesComponentsTableName(from);
}

function hasCardImage(row: Record<string, unknown> | null | undefined): boolean {
  return Boolean(pickFirstNonEmptyImageField(row?.card_image));
}

function normalizeIdList(postIds: unknown): string[] {
  if (Array.isArray(postIds)) {
    return postIds.map((id) => String(id)).filter(Boolean);
  }
  if (typeof postIds === "string") {
    return postIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Website-template collections store member rows in `template_components`, but
 * fork historically omitted `card_image`. Fill empty covers from the matching
 * stayuptodate row (same `unique_id`) so carousel tiles show media.
 */
async function enrichEmptyTemplateCardImages(
  userId: string,
  rows: any[],
): Promise<any[]> {
  const missing = rows.filter(
    (row) =>
      row &&
      !hasCardImage(row) &&
      typeof row.unique_id === "string" &&
      row.unique_id.trim(),
  );
  if (!missing.length) return rows;

  const uniqueIds = [
    ...new Set(missing.map((row) => String(row.unique_id).trim())),
  ];
  const { data: libRows, error } = await supabase
    .from("stayuptodate_components")
    .select("unique_id, card_image")
    .eq("user_id", userId)
    .in("unique_id", uniqueIds)
    .neq("status", "deleted");

  if (error || !libRows?.length) return rows;

  const byUnique = new Map<string, string>();
  for (const lib of libRows) {
    const uid = String((lib as any).unique_id ?? "").trim();
    const image = pickFirstNonEmptyImageField((lib as any).card_image);
    if (uid && image) byUnique.set(uid, image);
  }
  if (!byUnique.size) return rows;

  const backfills: Promise<unknown>[] = [];
  const next = rows.map((row) => {
    if (!row || hasCardImage(row)) return row;
    const uid = String(row.unique_id ?? "").trim();
    const image = uid ? byUnique.get(uid) : undefined;
    if (!image) return row;
    if (row.id != null) {
      backfills.push(
        supabase
          .from("template_components")
          .update({ card_image: image, isEmpty: false })
          .eq("id", row.id)
          .eq("user_id", userId)
          .then(() => undefined),
      );
    }
    return { ...row, card_image: image, media_url: row.media_url || image };
  });

  if (backfills.length) {
    void Promise.allSettled(backfills);
  }
  return next;
}

/** Hide a just-deleted carousel in every open collection strip. */
export function dropMemberFromCollectionComponentCache(
  queryClient: ReturnType<typeof useQueryClient>,
  memberId: unknown,
) {
  const id = String(memberId ?? "").trim();
  if (!id) return;
  queryClient.setQueriesData(["collectionComponents"], (old: any) => {
    if (!old || !Array.isArray(old.data)) return old;
    const data = old.data.filter((row: any) => String(row?.id ?? "") !== id);
    if (data.length === old.data.length) return old;
    return { ...old, data, total_count: data.length };
  });
}

function collectCachedRows(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
): Map<string, any> {
  const byId = new Map<string, any>();
  const cached = queryClient.getQueriesData<any>([
    "collectionComponents",
    userId,
  ]);
  for (const [, payload] of cached) {
    const rows = payload?.data;
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (row?.id == null) continue;
      byId.set(String(row.id), row);
    }
  }
  return byId;
}

function orderRows(
  rows: any[],
  postIds: string[],
  contentOrder: any,
  customOrder: any,
): any[] {
  let data = Array.from(
    new Map(rows.map((post: any) => [String(post.id), post])).values(),
  );

  if (contentOrder === "CUSTOM") {
    let customOrderArray: string[] = [];
    if (typeof customOrder === "string" && customOrder.trim() !== "") {
      customOrderArray = customOrder
        .split(",")
        .map((id: string) => id.trim())
        .filter(Boolean);
    } else if (Array.isArray(customOrder)) {
      customOrderArray = customOrder.map((id: any) => String(id));
    }

    if (customOrderArray.length > 0) {
      const idToItem = new Map<string, any>();
      data.forEach((item: any) => {
        idToItem.set(String(item.id), item);
      });
      const ordered: any[] = [];
      customOrderArray.forEach((id: string) => {
        const item = idToItem.get(id);
        if (item) ordered.push(item);
      });
      const usedIdSet = new Set(customOrderArray.map((id: string) => String(id)));
      const remaining = data.filter(
        (item: any) => !usedIdSet.has(String(item.id)),
      );
      data = [...ordered, ...remaining];
    } else {
      data.sort(
        (a: any, b: any) =>
          postIds.indexOf(String(a.id)) - postIds.indexOf(String(b.id)),
      );
    }
  } else if (contentOrder === "ASC") {
    data.sort(
      (a: any, b: any) =>
        new Date(a?.created_at || 0).getTime() -
        new Date(b?.created_at || 0).getTime(),
    );
  } else if (contentOrder === "DESC" || !contentOrder) {
    // Prefer explicit post_ids order when the user just added/removed members
    // (newest selection first from the picker). Fall back to created_at.
    const allInPostOrder = postIds.every((id) =>
      data.some((row) => String(row.id) === id),
    );
    if (allInPostOrder && postIds.length === data.length) {
      data.sort(
        (a: any, b: any) =>
          postIds.indexOf(String(a.id)) - postIds.indexOf(String(b.id)),
      );
    } else {
      data.sort(
        (a: any, b: any) =>
          new Date(b?.created_at || 0).getTime() -
          new Date(a?.created_at || 0).getTime(),
      );
    }
  } else {
    // Unknown order — keep membership sequence from post_ids.
    data.sort(
      (a: any, b: any) =>
        postIds.indexOf(String(a.id)) - postIds.indexOf(String(b.id)),
    );
  }

  return data;
}

export function useLoadCollectionComponents(
  username: any,
  user_id: any,
  postIds: any,
  title: any,
  content_order: any,
  custom_order: any,
  activeFilter?: any,
  from?: string,
) {
  const queryClient = useQueryClient();
  const normalizedPostIds = normalizeIdList(postIds);
  const postIdsKey = normalizedPostIds.join(",");

  async function getPaginatedFeed() {
    const post_ids = normalizedPostIds;
    if (!post_ids.length) {
      return { data: [], total_count: 0 };
    }

    // Reuse already-loaded member rows so Add concatenates and Remove just
    // filters — avoid refetching / remounting the whole strip.
    const cachedById = collectCachedRows(queryClient, String(user_id));
    const missingIds = post_ids.filter((id) => !cachedById.has(String(id)));

    if (missingIds.length > 0) {
      const tableName = getComponentsTableName(from);
      const activePageId = getActiveCreatorPageId();
      let query = supabase
        .from(tableName)
        .select("*")
        .eq("user_id", user_id)
        .in("id", missingIds)
        .eq("isEmpty", false)
        .neq("type", "highlight")
        .neq("display_name", "Inline Newsletter")
        .neq("display_name", "Popup Newsletter")
        .neq("display_name", "Form")
        .neq("display_name", "My Store Product")
        .neq("display_name", "My Links")
        .neq("type", "links-section")
        .neq("display_name", "My Store Product")
        .neq("type", "embed-form")
        .neq("type", "newsletter-form")
        .neq("type", "banner")
        .neq("type", "campaign")
        .neq("status", "deleted");

      if (isCreatorPagesMode(from) && activePageId) {
        query = query.eq("page_id", activePageId);
      }

      const { data: content, error: contentError }: any = await query;
      if (contentError) {
        throw new Error(contentError.message);
      }
      for (const row of content || []) {
        if (row?.id != null) cachedById.set(String(row.id), row);
      }
    }

    const merged = post_ids
      .map((id) => cachedById.get(String(id)))
      .filter(Boolean);

    let data = orderRows(merged, post_ids, content_order, custom_order);

    if (data && Array.isArray(data)) {
      let rows = data.slice(0, StaticVariables().MAX_COUNT);
      if (from === WEBSITE_TEMPLATE_SURFACE && user_id) {
        rows = await enrichEmptyTemplateCardImages(String(user_id), rows);
        const stillMissing = rows.filter(
          (row) => row && !hasCardImage(row) && row.id != null,
        );
        if (stillMissing.length && username) {
          const filled = await Promise.all(
            stillMissing.map((row) =>
              syncTemplateComponentCardImageIfNeeded(
                String(user_id),
                String(username),
                String(row.id),
              ),
            ),
          );
          const byId = new Map<string, string>();
          stillMissing.forEach((row, i) => {
            const url = filled[i];
            if (url) byId.set(String(row.id), url);
          });
          if (byId.size) {
            rows = rows.map((row) => {
              const url = row?.id != null ? byId.get(String(row.id)) : undefined;
              if (!url) return row;
              return {
                ...row,
                card_image: url,
                media_url: row.media_url || url,
              };
            });
          }
        }
      }
      return {
        data: rows,
        total_count: post_ids.length,
      };
    }
    throw new Error("Failed to fetch posts");
  }

  const queryResult = useQuery(
    [
      "collectionComponents",
      user_id,
      postIdsKey,
      title,
      content_order,
      custom_order,
      activeFilter,
      from,
      getActiveCreatorPageId() || "",
    ],
    getPaginatedFeed,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      // Keep existing tiles visible while only the newly added member fetches.
      keepPreviousData: true,
      enabled:
        Boolean(title) &&
        Boolean(user_id) &&
        Boolean(normalizedPostIds.length > 0),
    },
  );

  const forceRefetch = () => {
    queryClient.invalidateQueries([
      "collectionComponents",
      user_id,
    ]);
  };

  return { ...queryResult, forceRefetch };
}
